import {
  OptionalStringParam,
  useParam,
} from "@redotech/react-router-util/param";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import ClockRewindIcon from "@redotech/redo-icons/clock-rewind_filled.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getFlowVersions } from "@redotech/redo-merchant-app-common/client/team";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type {
  RedoListItem,
  RedoListItemSelectedSource,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { formatDateTime } from "@redotech/redo-web/time";
import { memo, useCallback, useMemo, useState } from "react";
import { FLOW_EDITOR_VERSION_PARAM } from "../../../app/ask-redo/components/tool-renderers/parse-flow-edit-preview";

interface FlowVersion {
  _id: string;
  name: string;
  publishedAt: string;
}

interface FlowVersionHistoryProps {
  flowType: FlowType;
}

export const FlowVersionHistory = memo(function FlowVersionHistory({
  flowType,
}: FlowVersionHistoryProps) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoMerchantClientContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [versionId, setVersionId] = useParam(
    new OptionalStringParam(FLOW_EDITOR_VERSION_PARAM),
  );

  const versionsLoad = useLoad(
    async (signal) => {
      const { flows } = await getFlowVersions(client, flowType, { signal });
      return (flows as unknown as FlowVersion[]).sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
    },
    [client, flowType],
  );

  const items: RedoListItem<FlowVersion>[] = useMemo(() => {
    const versions = versionsLoad.value;
    if (!versions || versions.length <= 1) {
      return [
        {
          id: "empty",
          value: {} as FlowVersion,
          type: "text" as const,
          text: t("No previous versions"),
        },
      ];
    }
    return versions.map((v, i) => ({
      id: v._id,
      value: v,
      type: "text" as const,
      text: v.name || t("Unnamed version"),
      supportingText: formatDateTime(
        Temporal.Instant.fromEpochMilliseconds(
          new Date(v.publishedAt).getTime(),
        ),
      ),
      ...(i === 0
        ? { infoBadge: { text: t("Active"), color: "success" as const } }
        : versionId === v._id
          ? { infoBadge: { text: t("Viewing"), color: "gray" as const } }
          : {}),
    }));
  }, [t, versionId, versionsLoad.value]);

  const handleItemSelected = useCallback(
    ({
      item,
      index,
    }: {
      item: RedoListItem<FlowVersion>;
      source: RedoListItemSelectedSource;
      index: number;
      selected: boolean;
    }) => {
      if (item.id === "empty") {
        return;
      }
      setDropdownOpen(false);
      // The newest published version is the live editor; older ones open
      // read-only via the version query param.
      setVersionId(index === 0 ? undefined : item.value._id);
    },
    [setVersionId],
  );

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconLeading={ClockRewindIcon}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setDropdownOpen(!dropdownOpen);
        }}
        size="sm"
        text={t("Version history")}
      />
      <RedoListDropdown
        dropdownAnchor={anchorEl}
        dropdownOpen={dropdownOpen}
        dropdownPlacement="bottom-end"
        items={items}
        itemSelected={handleItemSelected}
        setDropdownOpen={setDropdownOpen}
        size="sm"
      />
    </>
  );
});
